import { useCallback } from 'react';
import { useAvailableCities } from 'hooks/useUserCityAccess';
import {
  useCompanyCityFilter,
  useCompanyRegionFilter,
  useCompanyStateFilter,
} from 'state/filterCompanies';

/**
 * Coordinates the company location filters (state -> region -> city).
 *
 * Unlike DocumentSearch, the company search API filters purely on city codes,
 * so selecting a region here auto-selects all of that region's cities (and
 * deselecting a region drops them). This keeps `cityFilter` as the single
 * source of truth fed to the API while letting the City dropdown narrow the
 * selection within the chosen regions.
 */
export const useCompanyLocationFilters = () => {
  const { stateFilter, setStateFilter } = useCompanyStateFilter();
  const { regionFilter, setRegionFilter } = useCompanyRegionFilter();
  const { cityFilter, setCityFilter } = useCompanyCityFilter();
  const { availableStates, availableRegions } = useAvailableCities();

  const getRegionCityCodes = useCallback(
    (regionCode: string) =>
      (availableRegions[regionCode]?.cities ?? []).map((city) => city.code),
    [availableRegions],
  );

  const selectRegions = useCallback(
    (nextRegions: string[]) => {
      const added = nextRegions.filter((code) => !regionFilter.includes(code));
      const removed = regionFilter.filter((code) => !nextRegions.includes(code));
      const removedCityCodes = new Set(removed.flatMap(getRegionCityCodes));
      const addedCityCodes = added.flatMap(getRegionCityCodes);

      setCityFilter((prev) => {
        const kept = prev.filter((code) => !removedCityCodes.has(code));

        return Array.from(new Set([...kept, ...addedCityCodes]));
      });
      setRegionFilter(nextRegions);
    },
    [regionFilter, getRegionCityCodes, setCityFilter, setRegionFilter],
  );

  const selectState = useCallback(
    (nextState: string) => {
      const regionsForState = Object.keys(availableStates[nextState]?.regions ?? {});
      setStateFilter(nextState);
      selectRegions(regionsForState);
    },
    [availableStates, setStateFilter, selectRegions],
  );

  return {
    stateFilter,
    regionFilter,
    cityFilter,
    setCityFilter,
    selectState,
    selectRegions,
  };
};
